import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { logoutProcessor } from "./helpers/logoutProcessor";
import { playSuccess, playError } from "./helpers/audioPlayer";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import HiddenScanInput from "./HiddenScanInput";
import FlowLoader from "./FlowLoader";
import ScanStatusIcons from "./ScanStatusIcons";
import ScanFlashOverlay from "./ScanFlashOverlay";
import FitWord from "./FitWord";
import { conveyorLogger } from "./helpers/logHandler";
import { apiFetch } from "../api/apiFetch";

type ConveyorManualSorterScanProps = {
    goBack: () => void;
};

const ConveyorManualSorterScan = ({ goBack }: ConveyorManualSorterScanProps) => {
    const navigator = useNavigate();
    const { t } = useTranslation();

    const [step, setStep] = useState<number>(1);
    const [scan, setScan] = useState<string>("");
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [itemSku, setItemSku] = useState<string>("");
    const [location, setLocation] = useState<string>("");
    const [chute, setChute] = useState<string>("");
    const [zone, setZone] = useState<string | null>(null);
    const [activeDate, setActiveDate] = useState<string>("");
    const [activeBatch, setActiveBatch] = useState<string>("");

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    const doScanItem = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        const scannedSku = val.trim();
        setScan("");
        setLoading(true);

        try {
            const res = await apiFetch(`/zonestorage/iteminfo/scan-at-conveyor`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ itemSku: scannedSku })
            });

            if (res.ok) {
                const data = await res.json();
                setItemSku(scannedSku);
                setLocation(data.chute);
                setChute(data.chute);
                setZone(data.zone ?? null);
                setActiveDate(data.activeDate);
                setActiveBatch(data.activeBatch);
                conveyorLogger("Newport", "Manually Scan Item", scannedSku, "Warehouse", "Hand", "success");
                setStep(2);
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                setStep(1);
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("scan_failed"));
                conveyorLogger("Newport", "Manually Scan Item", scannedSku, "Warehouse", "Hand", "error");
            }
        } catch {
            setStep(1);
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("scan_failed"));
            conveyorLogger("Newport", "Manually Scan Item", scannedSku, "Warehouse", "Hand", "error");
        }

        setLoading(false);
    }, [scan, navigator, t]);

    const doScanLocation = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        if (val.trim() !== location) {
            playError();
            toast.dismiss(); toast.error(t("wrong_location"));
            setScan("");
            conveyorLogger("Newport", "Manually Scan Item", val, "Warehouse", "Hand", "wrong location");
            return;
        }

        setScan("");
        setLoading(true);

        try {
            const res = await apiFetch(`/zonestorage/iteminfo/mark-scanned`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    itemSku: itemSku,
                    activeDate: activeDate,
                    activeBatch: activeBatch,
                    sorter: 1
                })
            });

            if (res.ok) {
                flashScanStatus("success");
                playSuccess();
                setStep(1);
                setItemSku("");
                setLocation("");
                setChute("");
                setZone(null);
                setActiveDate("");
                setActiveBatch("");
                conveyorLogger("Newport", "Manually Scan Item", val, "Warehouse", "Hand", "success");
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                const data = await res.json();
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("mark_failed"));
                conveyorLogger("Newport", "Manually Scan Item", val, "Warehouse", "Hand", "error");
            }
        } catch {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("mark_failed"));
            conveyorLogger("Newport", "Manually Scan Item", val, "Warehouse", "Hand", "error");
        }

        setLoading(false);
    }, [scan, location, itemSku, activeDate, activeBatch, navigator, t]);

    const handleSubmit = useCallback((scannedValue?: string) => {
        if (step === 1) {
            doScanItem(scannedValue);
        } else {
            doScanLocation(scannedValue);
        }
    }, [step, doScanItem, doScanLocation]);

    return (
        <div className="scan-panel" style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}>
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("manual_sorter_scan")}</h1>
                <button className="page-back-btn" onClick={goBack}><ArrowLeft size={20} /></button>
            </div>

            <div className={`scan-centre ${step === 2 ? "conveyor-search-centre" : ""}`}>
                {step === 1 && <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />}

                <div className="conveyor-scan-group" style={{ overflow: "hidden" }}>
                    {step === 1 && (
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {t("scan_item").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                    )}

                    {loading && <FlowLoader />}

                    {step === 2 && !loading && (
                        <div className={`scan-feedback-area conveyor-search-results ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            <span className="conveyor-search-label">{t("scan_chute").toUpperCase()}</span>
                            <h1 className="conveyor-search-result">{(chute || "").replace(/^(NP|PL)0/i, "")}</h1>
                            {zone !== null && (
                                <>
                                    <span className="conveyor-search-label conveyor-search-label-sub">{t("scan_zone").toUpperCase()}</span>
                                    <h2 className="conveyor-search-result-sub">{zone.replace(/^(NP|PL)-0*/i, "")}</h2>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {step === 2 && scanStatus === "success" && (
                <div className="s2-inline-status s2-inline-success"><CheckCircle size={140} strokeWidth={3} /></div>
            )}
            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleSubmit}
                disabled={loading}
                disableCamera
            />
        </div>
    );
};

export default ConveyorManualSorterScan;
